import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useTenant } from '../../../../app/context/tenant-context';
import { useTabManager } from '../../../context/tab-manager-context';
import AlertViewV2 from '../../../pages/alertView/alertView';

import { EmptyState } from './EmptyState';
import { MainTabContent } from './MainTabContent';
import { ResizableContentArea } from './ResizableContentArea';
import { SidePanel } from './SidePanel';

import { Panel, Separator } from '@controlrooms/components';
import type { PanelImperativeHandle } from '@controlrooms/components';
import { ViewType } from '@controlrooms/models';

/**
 * PanelLayoutV3 - Multi-tab architecture panel layout (Rendering Only)
 *
 * This component is responsible ONLY for rendering the UI.
 * URL routing logic is handled by AppCore.
 *
 * Component Hierarchy:
 * LayoutV3
 * └── AppConfigProvider
 *     └── TabManagerProvider
 *         ├── AppCore (URL routing controller - renders nothing)
 *         └── LayoutContainer
 *             ├── SidebarContainer
 *             │   └── Sidebar (uses TabManagerContext to create tabs)
 *             └── PanelContainer
 *                 └── PanelLayoutV3 (this component - rendering only)
 *                     └── ResizableContentArea
 *                         ├── SidePanel (left, 300px)
 *                         ├── Separator
 *                         └── MainTabContent (right, takes remaining width)
 */
export const PanelLayoutV3: React.FC = () => {
  const { tenant } = useTenant();
  const location = useLocation();
  const { tabs, activeTabId, activeViewState } = useTabManager();

  const isAnalyzeRoute =
    location.pathname === `/app/t/${tenant}/analyze` ||
    location.pathname === `/app/t/${tenant}/analyze/`;

  // Render active tab content only - memoized to prevent re-renders
  // Use a more stable key for memoization: tab.id + tab.type + tab.viewType
  // This ensures we only re-render when the actual tab properties change
  const activeTab = useMemo(() => {
    if (!activeTabId) return null;
    return tabs.find((tab) => tab.id === activeTabId);
  }, [tabs, activeTabId]);

  // Determine if side panel should be visible based on ViewState
  // For MANAGE tabs with subPage='monitors', hide side panel when viewing detail page (ensemble_id exists)
  // For MANAGE tabs with subPage='webhooks', always hide side panel
  // For VIEWS tabs with MONITOR viewType, hide side panel when viewing detail page (ensemble_family_id exists)
  const shouldShowSidePanel = useMemo(() => {
    if (!activeTab || !activeViewState) return true;

    // Hide side panel for webhooks
    if (activeTab.type === 'MANAGE' && activeTab.subPage === 'webhooks') {
      return false;
    }

    // For MANAGE tabs with monitors subPage, hide side panel when viewing detail page
    if (activeTab.type === 'MANAGE' && activeTab.subPage === 'monitors') {
      const manageView = activeViewState.view[ViewType.MANAGE_MONITOR];
      // Hide side panel if ensemble_id is set (detail page) or if we're in a nested tab
      const hasDetailPage = !!manageView?.ensemble_id;
      const hasNestedTabs = manageView?.tabPath && manageView.tabPath.length > 0;
      return !hasDetailPage && !hasNestedTabs;
    }

    // Default: show side panel
    return true;
  }, [activeTab, activeViewState]);

  const sidePanelRef = useRef<PanelImperativeHandle | null>(null);

  useEffect(() => {
    if (tabs.length > 0 && shouldShowSidePanel) {
      sidePanelRef.current?.expand();
    } else {
      sidePanelRef.current?.collapse();
    }
  }, [shouldShowSidePanel, tabs.length]);

  const mainContent =
    tabs.length > 0 && activeTab ? (
      <MainTabContent tab={activeTab} />
    ) : (
      <Panel
        id="mainContent"
        minSize="200px"
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <EmptyState />
      </Panel>
    );

  const contentArea = (
    <ResizableContentArea>
      <SidePanel panelRef={sidePanelRef} defaultSize={tabs.length > 0 ? '350px' : '0px'} />
      <Separator
        style={
          !(tabs.length > 0 && shouldShowSidePanel)
            ? { flex: '0 0 0', minWidth: 0, width: 0, overflow: 'hidden', pointerEvents: 'none' }
            : undefined
        }
      />
      {mainContent}
    </ResizableContentArea>
  );

  return (
    <div
      data-testid="panel-layout-v3"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      {isAnalyzeRoute && <AlertViewV2 />}
      {contentArea}
    </div>
  );
};
